// components/faq-section.tsx
"use client";

import { useState } from "react";
import Link from "next/link";
import { ChevronDown } from "lucide-react";
import { motion, AnimatePresence, type Variants } from "framer-motion";

const faqs = [
  {
    q: "How do I book an experience with LilyLanka?",
    a: "Browse through our thoughtfully curated collection of experiences, select the activity that speaks to you, and tap Book Now. Our booking process is simple, intuitive, and designed to save you time.",
  },
  {
    q: "Can I modify or reschedule my booking?",
    a: "Yes. You may manage your booking through the My Trips section in the app, or contact our support team for personal assistance.",
  },
  {
    q: "What payment methods are available?",
    a: "We accept debit cards, credit cards, mobile payments, and local bank transfers.",
  },
  {
    q: "How long do refunds take to process?",
    a: "Refunds are typically processed within five to seven working days, depending on your payment provider.",
  },
  {
    q: "Will I receive confirmation after booking?",
    a: "Yes. You'll receive an immediate email confirmation and an in-app notification once your booking is complete.",
  },
];

const containerVariants: Variants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.08 } },
};

const itemVariants: Variants = {
  hidden: { opacity: 0, y: 16 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: "easeOut" } },
};

export default function FAQSection() {
  const [openIdx, setOpenIdx] = useState<number | null>(null);

  return (
    <section className="bg-purple-100 dark:bg-[#0a0a0a] py-16 md:py-24">
      <div className="max-w-6xl mx-auto px-6">
        <div className="grid gap-10 lg:grid-cols-12">
          {/* Left: Title */}
          <motion.div
            className="lg:col-span-4"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.5, ease: "easeOut" }}
          >
            <span className="text-xs font-bold uppercase tracking-[0.18em] text-[#5b288f] dark:text-[#c084fc]">
              FAQ
            </span>
            <h2 className="mt-3 text-4xl md:text-5xl text-gray-900 dark:text-white leading-tight font-poppins">
              LilyLanka
              <br />
              Booking FAQs
            </h2>
            <p className="mt-4 text-sm text-gray-600 dark:text-gray-400 max-w-xs">
              Everything you need to know before you book your next trip.
            </p>
          </motion.div>

          {/* Right: Accordion */}
          <motion.div
            className="lg:col-span-8"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
          >
            <div className="border-t border-gray-200 dark:border-gray-700">
              {faqs.map((faq, idx) => {
                const isOpen = openIdx === idx;
                return (
                  <motion.div
                    key={idx}
                    variants={itemVariants}
                    className="border-b border-gray-200 dark:border-gray-700"
                  >
                    <button
                      onClick={() => setOpenIdx(isOpen ? null : idx)}
                      aria-expanded={isOpen}
                      className="w-full flex items-center justify-between gap-4 py-6 px-2 -mx-2 text-left rounded-lg transition-colors hover:bg-gray-50 dark:hover:bg-white/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#5b288f]/40"
                    >
                      <span
                        className={`text-base transition-colors ${
                          isOpen
                            ? "font-semibold text-[#5b288f] dark:text-[#c084fc]"
                            : "text-gray-900 dark:text-white"
                        }`}
                      >
                        {faq.q}
                      </span>
                      <motion.span
                        animate={{ rotate: isOpen ? 180 : 0 }}
                        transition={{ duration: 0.25, ease: "easeOut" }}
                        className="flex-shrink-0"
                      >
                        <ChevronDown
                          className={`h-5 w-5 ${
                            isOpen
                              ? "text-[#5b288f] dark:text-[#c084fc]"
                              : "text-gray-500 dark:text-gray-400"
                          }`}
                        />
                      </motion.span>
                    </button>

                    <AnimatePresence initial={false}>
                      {isOpen && (
                        <motion.div
                          key="content"
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3, ease: "easeInOut" }}
                          className="overflow-hidden px-2 -mx-2"
                        >
                          <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed pb-6">
                            {faq.a}
                          </p>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                );
              })}
            </div>

            <Link
              href="/footer/help/faq"
              className="mt-6 inline-flex items-center gap-1 text-sm font-semibold text-[#5b288f] dark:text-[#c084fc] hover:underline"
            >
              View more FAQs →
            </Link>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
